import { ChangeDetectionStrategy, Component, Type, computed, input, output, signal } from '@angular/core';
import { NgComponentOutlet } from '@angular/common';
import { LucideArrowDown, LucideArrowUp, LucideArrowUpDown } from '@lucide/angular';
import { SkeletonLoaderComponent } from '../skeleton-loader/skeleton-loader.component';
import { EmptyStateComponent } from '../empty-state/empty-state.component';

export type TableRow = Record<string, string | number | null | undefined>;

export interface TableColumn {
  key: string;
  label: string;
  sortable?: boolean;
  align?: 'left' | 'right';
}

export type SortDirection = 'asc' | 'desc';

export interface SortState {
  key: string;
  direction: SortDirection;
}

/** Sorts client-side; rows collapse into labeled cards on narrow screens. */
@Component({
  selector: 'ds-data-table',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgComponentOutlet, SkeletonLoaderComponent, EmptyStateComponent],
  template: `
    @if (loading()) {
      <ds-skeleton-loader variant="list" [count]="4" [loadingLabel]="'Memuat ' + caption()" />
    } @else if (rows().length === 0) {
      <ds-empty-state [title]="emptyTitle()" [description]="emptyDescription()" />
    } @else {
      <div class="wrap">
        <table class="table">
          <caption class="sr-only">{{ caption() }}</caption>
          <thead>
            <tr>
              @for (column of columns(); track column.key) {
                <th
                  scope="col"
                  [class.table__th--right]="column.align === 'right'"
                  [attr.aria-sort]="ariaSort(column)"
                >
                  @if (column.sortable) {
                    <button type="button" class="sort" (click)="toggleSort(column.key)">
                      <span>{{ column.label }}</span>
                      <span class="sort__icon" aria-hidden="true">
                        <ng-container
                          [ngComponentOutlet]="sortIcon(column.key)"
                          [ngComponentOutletInputs]="{ size: 18, strokeWidth: 2.25 }"
                        />
                      </span>
                    </button>
                  } @else {
                    {{ column.label }}
                  }
                </th>
              }
            </tr>
          </thead>
          <tbody>
            @for (row of sortedRows(); track $index) {
              <tr class="table__row">
                @for (column of columns(); track column.key) {
                  <td
                    [class.table__td--right]="column.align === 'right'"
                    [attr.data-label]="column.label"
                  >
                    {{ row[column.key] ?? '—' }}
                  </td>
                }
              </tr>
            }
          </tbody>
        </table>
      </div>
    }
  `,
  styles: `
    .sr-only {
      position: absolute;
      width: 1px;
      height: 1px;
      margin: -1px;
      clip-path: inset(50%);
      overflow: hidden;
    }

    .wrap {
      background: var(--color-surface);
      border: 1.5px solid var(--color-border-subtle);
      border-radius: var(--radius-lg);
      overflow-x: auto;
      max-width: 100%;
    }

    .table {
      width: 100%;
      max-width: 100%;
      border-collapse: collapse;
      font-size: var(--text-md);
    }

    th {
      padding: 14px;
      background: #F8FAFC;
      text-align: left;
      font-size: 14px;
      font-weight: var(--font-weight-bold);
      color: var(--color-text);
      white-space: nowrap;
    }

    .table__row:hover td {
      background: oklch(97% 0.014 254.604);
    }

    .table__th--right,
    .table__td--right {
      text-align: right;
    }

    td {
      padding: var(--space-2) var(--space-3);
      border-top: 1.5px solid var(--color-border-subtle);
      color: var(--color-text);
      background: #ffffff;
    }

    .sort {
      display: inline-flex;
      align-items: center;
      gap: var(--space-1);
      min-height: var(--touch-target);
      margin: calc(var(--space-2) * -1) 0;
      padding: 0 var(--space-1);
      border: 0;
      border-radius: var(--radius-sm);
      background: transparent;
      font: inherit;
      color: inherit;
      cursor: pointer;

      &:hover {
        color: var(--color-primary);
      }
    }

    .sort__icon {
      display: inline-flex;
      color: var(--color-text-secondary);
    }

    /* stacked cards on narrow screens */
    @media (max-width: 719px) {
      thead {
        position: absolute;
        width: 1px;
        height: 1px;
        margin: -1px;
        clip-path: inset(50%);
        overflow: hidden;
      }

      .table,
      tbody,
      tr,
      td {
        display: block;
      }

      .table__row {
        padding: var(--space-2) 0;

        & + .table__row {
          border-top: 1.5px solid var(--color-border-subtle);
        }
      }

      td {
        display: flex;
        justify-content: space-between;
        gap: var(--space-3);
        border-top: 0;
        padding: var(--space-2) var(--space-4);
        text-align: right;

        &::before {
          content: attr(data-label);
          font-weight: var(--font-weight-bold);
          color: var(--color-text-secondary);
          font-size: var(--text-sm);
          text-align: left;
        }
      }

      .table__row:nth-child(even) td {
        background: transparent;
      }

      .table__td--right {
        text-align: right;
      }
    }
  `,
})
export class DataTableComponent {
  columns = input.required<TableColumn[]>();
  rows = input.required<TableRow[]>();
  /** Read by screen readers; describe the table's contents. */
  caption = input.required<string>();
  loading = input(false);
  emptyTitle = input('Belum ada data');
  emptyDescription = input<string | undefined>(undefined);
  sortChanged = output<SortState>();

  protected readonly sort = signal<SortState | null>(null);

  protected readonly sortedRows = computed(() => {
    const state = this.sort();
    const rows = this.rows();
    if (!state) return rows;

    const factor = state.direction === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const left = a[state.key];
      const right = b[state.key];
      if (left == null) return 1;
      if (right == null) return -1;
      if (typeof left === 'number' && typeof right === 'number') {
        return (left - right) * factor;
      }
      return String(left).localeCompare(String(right), 'id') * factor;
    });
  });

  protected toggleSort(key: string): void {
    const current = this.sort();
    const next: SortState =
      current?.key === key && current.direction === 'asc'
        ? { key, direction: 'desc' }
        : { key, direction: 'asc' };
    this.sort.set(next);
    this.sortChanged.emit(next);
  }

  protected ariaSort(column: TableColumn): string | null {
    if (!column.sortable) return null;
    const state = this.sort();
    if (state?.key !== column.key) return 'none';
    return state.direction === 'asc' ? 'ascending' : 'descending';
  }

  protected sortIcon(key: string): Type<unknown> {
    const state = this.sort();
    if (state?.key !== key) return LucideArrowUpDown;
    return state.direction === 'asc' ? LucideArrowUp : LucideArrowDown;
  }
}
